import NavigationButton from '../NavigationButton/NavigationButton';
import BugButton from '../BugButton/BugButton';
import SignInButton from '../Login/SignInButton';
import SignOutButton from '../Login/SignOutButton';
import { useIsAuthenticated } from '@azure/msal-react';
import { getBucketViewSystemID } from './headerHelpers';
import SystemProgressBar from '../SystemProgressBar/SystemProgressBar';
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch';

import useSystemID from '../../hooks/useSystemID';
import useMsalContactData from '../../hooks/useMsalContactData';
import useIsDev from '../../hooks/useIsDev';

import { makeRequest } from '../../utils';
import {
  useStreamlineContext,
  useToggleStreamlineContext,
  useFieldsChangedContext,
  useSetFieldsChangedContext
} from '../../contexts/ContextProvider';
import config from '../../config/index';
import { useCallback } from 'react';

export default function Header({ titleParams }) {
  const system_id = useSystemID();
  const isAuthenticated = useIsAuthenticated();
  const isDev = useIsDev(isAuthenticated);
  const isStreamline = useStreamlineContext();
  const toggleStreamline = useToggleStreamlineContext();
  const fieldsChanged = useFieldsChangedContext();
  const setFieldsChanged = useSetFieldsChangedContext();
  const contactRole = useMsalContactData('role');
  const canToggleStreamline =
    config['canToggleStreamline'].includes(contactRole);

  const handleToggleStreamline = useCallback(() => {
    let proceed = true;
    if (Object.values(fieldsChanged).includes(true)) {
      const fieldsList = Object.keys(fieldsChanged)
        .filter((field) => fieldsChanged[field] === true)
        .reduce(
          (msg, field, idx) =>
            msg + `  ${idx + 1}. ${config.fieldsChangedLabels[field]} \n`,
          ''
        );

      proceed = window.confirm(
        `Unsaved fields will be lost.\nThe following forms have been changed, but not saved.\n${fieldsList}\n Confirm to proceed.`
      );
    }

    if (proceed) {
      const url = `/nld_tracker/update_streamline`;
      const requestArgs = {
        method: 'PATCH',
        body: JSON.stringify({
          system_id,
          streamline: !isStreamline
        })
      };
      const contentType = 'application/json';
      makeRequest(url, requestArgs, contentType).then(() => {
        toggleStreamline();
        setFieldsChanged(config.fieldsChangedInit);
      });
    }
  }, [
    system_id,
    fieldsChanged,
    isStreamline,
    setFieldsChanged,
    toggleStreamline
  ]);

  const switchParams = {
    disabled: !canToggleStreamline,
    tooltip: (
      <p>
        This slider changes the workflow between Original and Streamline.
        <br />
        {canToggleStreamline
          ? 'Only use when directed to do so.'
          : 'Please contact a reviewer or PTS lead to switch workflows'}
      </p>
    )
  };

  if (isAuthenticated) {
    const pname = window.location.pathname;
    if (pname === '/') {
      return (
        <div className='header'>
          <div className='header-inner'>
            <div className='header-nav-left'>
              <SignOutButton />
              <BugButton page='Home' />
            </div>

            <h1 className='styled-title'>
              Levee Finder{' '}
              {isDev ? <div className='devsite'>(Dev Site)</div> : null}
            </h1>
            <div className='header-nav-right'>
              <NavigationButton url='/dashboard' text='Dashboard' />
              <NavigationButton url='/bucketview' text='BucketView' />
            </div>
          </div>
        </div>
      );
    } else if (pname === '/dashboard') {
      return (
        <div className='header'>
          <div className='header-inner'>
            <div className='header-nav-left'>
              <SignOutButton />
              <BugButton page='Dashboard' />
            </div>
            <h1 className='styled-title'>
              Dashboard{' '}
              {isDev ? <div className='devsite'>(Dev Site)</div> : null}
            </h1>
            <div className='header-nav-right'>
              <NavigationButton url='/' text='Home' />
              <NavigationButton url='/bucketview' text='BucketView' />
            </div>
          </div>
        </div>
      );
    } else if (pname.startsWith('/edit')) {
      const systemID = pname.substring(pname.lastIndexOf('/') + 1);
      return (
        <div className='header'>
          <div className='header-inner'>
            <div className='header-nav-left'>
              <SignOutButton />
              <BugButton page='Edit' systemID={systemID} />
            </div>
            <h1
              className='styled-title'
              style={{
                color: 'black',
                minWidth: '450px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              System ID: <span>{systemID}</span>
              {isDev ? <div className='devsite'>(Dev Site)</div> : null}
              <ToggleSwitch
                isChecked={isStreamline}
                onClick={handleToggleStreamline}
                offText='Original'
                onText='Streamline'
                disabled={switchParams.disabled}
                toolTip={switchParams.tooltip}
              />
            </h1>
            <div className='header-nav-right'>
              <NavigationButton url='/' text='Home' />
              <NavigationButton url='/dashboard' text='Dashboard' />
              <NavigationButton url='/bucketview' text='BucketView' />
            </div>
          </div>
          <SystemProgressBar
            status={
              titleParams
                ? {
                    Status: titleParams['Status'],
                    'Detailed Status': titleParams['Detailed Status']
                  }
                : undefined
            }
          />
        </div>
      );
    } else if (pname.startsWith('/bucketview')) {
      let systemID = getBucketViewSystemID();
      return (
        <div className='header'>
          <div className='header-inner'>
            <div className='header-nav-left'>
              <SignOutButton />
              <BugButton page='BucketView' systemID={systemID} />
            </div>
            <h1 className='styled-title'>
              BucketView{' '}
              {isDev ? <div className='devsite'>(Dev Site)</div> : null}
            </h1>
            <div className='header-nav-right'>
              <NavigationButton url='/' text='Home' />
              {systemID !== undefined && (
                <NavigationButton
                  url={`/edit/${systemID}`}
                  text='System Data'
                />
              )}
              <NavigationButton url='/dashboard' text='Dashboard' />
            </div>
          </div>
        </div>
      );
    }
    return <></>;
  }
  return (
    <div className='header'>
      <div className='header-inner'>
        <div className='header-nav-left'>
          <SignInButton />
        </div>
        <h1 className='styled-title'>NLD Refinement Web Application</h1>
      </div>
    </div>
  );
}
